import { useId } from 'react'
import type { InputHTMLAttributes, ReactElement } from 'react'

import styles from './inputText.module.scss'

export type InputTextProps = InputHTMLAttributes<HTMLInputElement> & {
  /** Required, and rendered as a real `<label>`: a placeholder is not a label —
   * it vanishes as soon as somebody types, which on a plant tablet means the
   * field stops saying what it holds halfway through being filled in. */
  label: string
  /** A permanent hint under the field (a format, a rule). */
  hint?: string
}

/**
 * The kit's text field. No per-field `error`: every screen renders ONE form-level
 * `<p role="alert">` with the Spanish copy for the tRPC code it got back, because
 * that is where the failures actually arrive — a CONFLICT on a group name, a
 * password the API refused. A second place to put a message is a second thing to
 * keep in step.
 */
export const InputText = ({ label, hint, id, className, ...rest }: InputTextProps): ReactElement => {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const hintId = `${inputId}-hint`

  return (
    <div className={[styles['input-text'], className ?? ''].filter(Boolean).join(' ')}>
      <label className={styles['input-text__label']} htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        className={styles['input-text__field']}
        aria-describedby={hint !== undefined ? hintId : undefined}
        {...rest}
      />
      {hint !== undefined && (
        <p id={hintId} className={styles['input-text__hint']}>{hint}</p>
      )}
    </div>
  )
}
